import React from "react";
import {
  CaretDownOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
  CloseOutlined,
  DownloadOutlined,
  FileTextFilled,
  LeftOutlined,
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useSmartBack } from "../utils/useSmartBack";
import NavigationDropdown from "./NavigationDropDown";
import { exportToExcel, readExcelFile } from "../utils/exportUtils";
import { normalizeSortRules, type SortRule } from "../utils/sortRules";

export interface ExportColumnDef {
  label: string;
  key: string;
}

interface CommonHeaderProps {
  backTo?: string | number;
  className?: string;
  currentRecord?: string | number;
  disableNext?: boolean;
  disablePrevious?: boolean;
  exportColumns?: ExportColumnDef[];
  exportFilename?: string;
  getData?: () => Record<string, any>[];
  hideNavigationDropdown?: boolean;
  isFindMode?: boolean;
  onBack?: () => void;
  onCancelFind?: () => void;
  onDeleteRecord?: () => void;
  onFind?: () => void;
  onImport?: (rows: Record<string, any>[]) => Promise<void>;
  onNewRecord?: () => void;
  onNextRecord?: () => void;
  onPerformFind?: () => void;
  onPreviousRecord?: () => void;
  onRecordChange?: (value: string) => void;
  onRecordCommit?: (value: string) => void;
  onRecordSliderCommit?: (index: number) => void;
  onShowAll?: () => void;
  onApplySort?: (rules: SortRule[]) => void;
  onSort?: () => void;
  recordCount?: number;
  recordIndex?: number;
  recordInputLabel?: string;
  rightContent?: React.ReactNode;
  showAllDisabled?: boolean;
  sortColumns?: ExportColumnDef[];
  sortRules?: SortRule[];
  sortTitle?: string;
  title?: string;
  totalActiveCount?: number;
  totalLabel?: string;
  totalRecordCount?: number;
  totalValue?: string | number;
  withQuoteId?: (path: string) => string;
}

const toolbarButtonStyle: React.CSSProperties = {
  backgroundColor: "#d4d0c8",
  border: "1px outset #ffffff",
  borderRadius: 0,
  height: 28,
  width: 30,
};

const iconColor = "#70889d";

const HeaderAction: React.FC<{
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}> = ({ disabled = false, icon, label, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="common-header-action-btn flex h-[50px] min-w-[58px] flex-col items-center justify-center gap-[2px] border-0 bg-transparent px-2 text-[10px] leading-none text-black hover:opacity-75 disabled:cursor-not-allowed disabled:opacity-40"
  >
    <span className="relative flex h-6 items-center justify-center text-[20px] leading-none">
      {icon}
    </span>
    <span className="whitespace-nowrap">{label}</span>
  </button>
);

// ─── Shared dialog primitives (gray Windows style) ───────────────────────────

const DLG_BG = "#f0f0f0";
const DLG_BORDER = "2px solid #808080";
const DLG_SHADOW = "3px 3px 8px rgba(0,0,0,0.45)";
const DLG_FONT: React.CSSProperties = { fontFamily: "Arial, sans-serif", fontSize: 12, color: "#000" };

const titleBarStyle: React.CSSProperties = {
  background: "linear-gradient(to right, #6b6b6b, #4a4a4a)",
  color: "#fff",
  fontSize: 12,
  fontWeight: "bold",
  padding: "4px 8px",
  marginBottom: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  userSelect: "none",
};

const TitleBar: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <div style={titleBarStyle}>
    <span>{title}</span>
    <div style={{ display: "flex", gap: 4 }}>
      <button type="button" style={{ width: 16, height: 14, fontSize: 9, lineHeight: 1, background: "#c0c0c0", border: "1px outset #fff", cursor: "pointer", color: "#000" }} title="Help">?</button>
      <button type="button" style={{ width: 16, height: 14, fontSize: 9, lineHeight: 1, background: "#c0c0c0", border: "1px outset #fff", cursor: "pointer", color: "#000" }} onClick={onClose} title="Close">✕</button>
    </div>
  </div>
);

const grayListBox: React.CSSProperties = {
  width: 190,
  height: 200,
  border: "2px inset #808080",
  backgroundColor: "#ffffff",
  overflowY: "auto",
  fontSize: 12,
  fontFamily: "Arial, sans-serif",
};

const grayListItem = (selected: boolean): React.CSSProperties => ({
  padding: "2px 6px",
  cursor: "pointer",
  backgroundColor: selected ? "#808080" : "transparent",
  color: selected ? "#ffffff" : "#000000",
  userSelect: "none",
  whiteSpace: "nowrap",
});

const grayBtn = (width = 90): React.CSSProperties => ({
  width,
  height: 23,
  fontSize: 12,
  fontFamily: "Arial, sans-serif",
  backgroundColor: "#d4d0c8",
  border: "2px outset #ffffff",
  borderRadius: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#000",
});

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.3)",
};

// ─── Export Dialog ────────────────────────────────────────────────────────────

const ExportDialog: React.FC<{
  columns: ExportColumnDef[];
  getData: () => Record<string, any>[];
  filename: string;
  onClose: () => void;
}> = ({ columns, getData, filename, onClose }) => {
  const [available, setAvailable] = React.useState<ExportColumnDef[]>(columns);
  const [selected, setSelected] = React.useState<ExportColumnDef[]>([]);
  const [leftHighlight, setLeftHighlight] = React.useState<string | null>(null);
  const [rightHighlight, setRightHighlight] = React.useState<string | null>(null);

  const moveOne = () => {
    if (!leftHighlight) return;
    const col = available.find((c) => c.key === leftHighlight);
    if (!col) return;
    setAvailable((prev) => prev.filter((c) => c.key !== leftHighlight));
    setSelected((prev) => [...prev, col]);
    setLeftHighlight(null);
  };

  const moveAll = () => {
    setSelected((prev) => [...prev, ...available]);
    setAvailable([]);
    setLeftHighlight(null);
  };

  const clearAll = () => {
    setAvailable(columns);
    setSelected([]);
    setLeftHighlight(null);
    setRightHighlight(null);
  };

  const handleExport = () => {
    const cols = selected.length > 0 ? selected : columns;
    const data = getData();
    if (data.length === 0) { message.warning("No records to export."); return; }
    exportToExcel(data, cols, filename);
    message.success(`Exported ${data.length} record(s) to ${filename}.xlsx`);
    onClose();
  };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ backgroundColor: DLG_BG, border: DLG_BORDER, boxShadow: DLG_SHADOW, padding: "0 0 10px", minWidth: 500, ...DLG_FONT }}>
        <TitleBar title="Specify Field Order for Export" onClose={onClose} />

        <div style={{ padding: "0 12px" }}>
          <p style={{ fontSize: 11, marginBottom: 8 }}>
            Select and move the fields to be exported.
          </p>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            {/* Left list */}
            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Available Fields</div>
              <ul style={{ ...grayListBox, listStyle: "none", margin: 0, padding: 0 }}>
                {available.map((col) => (
                  <li key={col.key} style={grayListItem(leftHighlight === col.key)}
                    onMouseDown={() => { setLeftHighlight(col.key); setRightHighlight(null); }}
                    onDoubleClick={() => { setLeftHighlight(col.key); setTimeout(moveOne, 0); }}>
                    {col.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Centre buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingTop: 22 }}>
              <button type="button" style={grayBtn()} onClick={moveOne} disabled={!leftHighlight}>Move</button>
              <button type="button" style={grayBtn()} onClick={moveAll} disabled={available.length === 0}>Move All</button>
              <button type="button" style={grayBtn()} onClick={clearAll} disabled={selected.length === 0}>Clear All</button>
            </div>

            {/* Right list */}
            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Field export order</div>
              <ul style={{ ...grayListBox, listStyle: "none", margin: 0, padding: 0 }}>
                {selected.map((col) => (
                  <li key={col.key} style={grayListItem(rightHighlight === col.key)}
                    onMouseDown={() => { setRightHighlight(col.key); setLeftHighlight(null); }}>
                    {col.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 10 }}>
            <button type="button" style={grayBtn(70)} onClick={handleExport}>Export</button>
            <button type="button" style={grayBtn(70)} onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Import Field Mapping Dialog ─────────────────────────────────────────────

const sortDialogListBox: React.CSSProperties = {
  width: 236,
  height: 250,
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #555",
};

const sortDialogListItem = (active: boolean): React.CSSProperties => ({
  padding: "3px 8px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  background: active ? "#0a64d8" : "transparent",
  color: active ? "#fff" : "#000",
});

const sortDialogHoverStyle = `
.sort-dialog-list-item:not(.sort-dialog-list-item-active):hover {
  background: #e6e6e6 !important;
}
.sort-dialog-button:not(:disabled):hover {
  background: #dcdcdc !important;
  border-color: #777 !important;
  box-shadow: inset 0 1px 0 #fff, 0 0 0 1px rgba(0, 0, 0, 0.12) !important;
}
.sort-dialog-button:not(:disabled):active {
  background: #cfcfcf !important;
}
.sort-dialog-button:disabled {
  cursor: not-allowed !important;
  opacity: 0.6;
}
`;

const sortDialogButton = (width = 110): React.CSSProperties => ({
  minWidth: width,
  height: 28,
  padding: "2px 10px",
  border: "1px solid #aaa",
  background: "#e5e5e5",
  color: "#000",
  boxShadow: "inset 0 1px 0 #fff",
  cursor: "pointer",
});

const SortDialogTitleBar: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
  <div
    style={{
      height: 30,
      padding: "0 8px 0 10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "linear-gradient(#777, #555)",
      color: "#fff",
      fontWeight: 700,
      fontSize: 14,
    }}
  >
    <span>{title}</span>
    <div style={{ display: "flex", gap: 4 }}>
      <button type="button" style={{ width: 20, height: 18, padding: 0, lineHeight: "16px" }}>?</button>
      <button type="button" style={{ width: 20, height: 18, padding: 0, lineHeight: "16px" }} onClick={onClose}>
        x
      </button>
    </div>
  </div>
);

const SortBarsIcon: React.FC<{ direction: SortRule["direction"] }> = ({ direction }) => {
  const heights = direction === "asc" ? [4, 7, 10] : [10, 7, 4];

  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2, width: 16, height: 12 }}>
      {heights.map((height, index) => (
        <span key={index} style={{ display: "inline-block", width: 3, height, background: "#000" }} />
      ))}
    </span>
  );
};

const SortDialog: React.FC<{
  columns: ExportColumnDef[];
  initialRules?: SortRule[];
  onApply: (rules: SortRule[]) => void;
  onClose: () => void;
  title?: string;
}> = ({ columns, initialRules = [], onApply, onClose, title = "Sort Records" }) => {
  const allowedKeys = React.useMemo(() => columns.map((col) => col.key), [columns]);
  const [selected, setSelected] = React.useState<SortRule[]>(() => normalizeSortRules(initialRules, allowedKeys));
  const [leftHighlight, setLeftHighlight] = React.useState<string | null>(null);
  const [rightHighlight, setRightHighlight] = React.useState<string | null>(() => selected[0]?.field ?? null);
  const [pendingDirection, setPendingDirection] = React.useState<SortRule["direction"]>(() => selected[0]?.direction || "asc");

  const available = React.useMemo(
    () => columns.filter((col) => !selected.some((rule) => rule.field === col.key)),
    [columns, selected],
  );
  const selectedColumns = React.useMemo(
    () => selected
      .map((rule) => ({ rule, column: columns.find((col) => col.key === rule.field) }))
      .filter((item): item is { rule: SortRule; column: ExportColumnDef } => Boolean(item.column)),
    [columns, selected],
  );
  const activeRule = selected.find((rule) => rule.field === rightHighlight) || selected[0];
  const activeDirection = activeRule?.direction || pendingDirection;

  const moveColumn = (columnKey: string) => {
    const col = available.find((c) => c.key === columnKey);
    if (!col) return;
    setSelected((prev) => [...prev, { field: col.key, direction: pendingDirection }]);
    setRightHighlight(col.key);
    setLeftHighlight(null);
  };

  const moveOne = () => {
    if (!leftHighlight) return;
    moveColumn(leftHighlight);
  };

  const moveAll = () => {
    const nextRules = available.map((col) => ({ field: col.key, direction: pendingDirection }));
    setSelected((prev) => [...prev, ...nextRules]);
    if (!rightHighlight && nextRules.length > 0) setRightHighlight(nextRules[0].field);
    setLeftHighlight(null);
  };

  const clearAll = () => {
    setSelected([]);
    setLeftHighlight(null);
    setRightHighlight(null);
  };

  const setDirection = (direction: SortRule["direction"]) => {
    setPendingDirection(direction);
    const field = rightHighlight || selected[0]?.field;
    if (!field) return;

    setRightHighlight(field);
    setSelected((prev) => prev.map((rule) => rule.field === field ? { ...rule, direction } : rule));
  };

  const handleApply = () => {
    onApply(normalizeSortRules(selected, allowedKeys));
    onClose();
  };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{sortDialogHoverStyle}</style>
      <div style={{ ...DLG_FONT, backgroundColor: "#eee", border: "1px solid #777", boxShadow: "0 2px 12px rgba(0,0,0,0.35)", padding: "0 0 10px", minWidth: 650 }}>
        <SortDialogTitleBar title={title} onClose={onClose} />

        <div style={{ padding: "12px 12px 0" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Available Fields</div>
              <ul style={{ ...sortDialogListBox, listStyle: "none", margin: 0, padding: 0 }}>
                {available.map((col) => (
                  <li
                    key={col.key}
                    className={leftHighlight === col.key ? "sort-dialog-list-item sort-dialog-list-item-active" : "sort-dialog-list-item"}
                    style={sortDialogListItem(leftHighlight === col.key)}
                    onMouseDown={() => { setLeftHighlight(col.key); setRightHighlight(null); }}
                    onDoubleClick={() => moveColumn(col.key)}
                  >
                    {col.label}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 24 }}>
              <button type="button" className="sort-dialog-button" style={sortDialogButton()} onClick={moveOne} disabled={!leftHighlight}>Move</button>
              <button type="button" className="sort-dialog-button" style={sortDialogButton()} onClick={moveAll} disabled={available.length === 0}>Move All</button>
              <button type="button" className="sort-dialog-button" style={sortDialogButton()} onClick={clearAll} disabled={selected.length === 0}>Clear All</button>
            </div>

            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Sort Order</div>
              <ul style={{ ...sortDialogListBox, listStyle: "none", margin: 0, padding: 0 }}>
                {selectedColumns.map(({ rule, column }) => (
                  <li
                    key={rule.field}
                    className={rightHighlight === rule.field ? "sort-dialog-list-item sort-dialog-list-item-active" : "sort-dialog-list-item"}
                    style={sortDialogListItem(rightHighlight === rule.field)}
                    onMouseDown={() => { setRightHighlight(rule.field); setLeftHighlight(null); }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <SortBarsIcon direction={rule.direction} />
                      {column.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5, margin: "10px 0 0 254px" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <SortBarsIcon direction="asc" />
              <input
                type="radio"
                checked={activeDirection === "asc"}
                onChange={() => setDirection("asc")}
                style={{ cursor: "pointer" }}
              />
              Ascending order
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <SortBarsIcon direction="desc" />
              <input
                type="radio"
                checked={activeDirection === "desc"}
                onChange={() => setDirection("desc")}
                style={{ cursor: "pointer" }}
              />
              Descending order
            </label>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <button type="button" className="sort-dialog-button" style={sortDialogButton(80)} onClick={handleApply}>Sort</button>
            <button type="button" className="sort-dialog-button" style={sortDialogButton(80)} onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};
interface FieldMapping {
  targetCol: ExportColumnDef;
  sourceHeader: string | null;
  include: boolean;
}

const mappingTdBase: React.CSSProperties = {
  padding: "1px 6px",
  fontSize: 12,
  fontFamily: "Arial, sans-serif",
  borderBottom: "1px solid #d8d8d8",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const ImportMappingDialog: React.FC<{
  filename: string;
  tableName: string;
  sourceHeaders: string[];
  sampleRow: Record<string, any>;
  sourceData: Record<string, any>[];
  targetColumns: ExportColumnDef[];
  onConfirm: (data: Record<string, any>[]) => void;
  onCancel: () => void;
}> = ({ filename, tableName, sourceHeaders, sampleRow, sourceData, targetColumns, onConfirm, onCancel }) => {
  // Collect all unique non-empty values per column across ALL rows
  const columnSamples = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const header of sourceHeaders) {
      const seen = new Set<string>();
      for (const row of sourceData) {
        const val = String(row[header] ?? "").trim();
        if (val) seen.add(val);
      }
      map[header] = Array.from(seen);
    }
    return map;
  }, [sourceHeaders, sourceData]);

  const buildInitial = (): FieldMapping[] =>
    targetColumns.map((tc, i) => {
      const positional = sourceHeaders[i] ?? null;
      return { targetCol: tc, sourceHeader: positional, include: positional !== null };
    });

  const [mappings, setMappings] = React.useState<FieldMapping[]>(buildInitial);

  const toggleInclude = (idx: number) => {
    setMappings((prev) => prev.map((m, i) => (i === idx ? { ...m, include: !m.include } : m)));
  };

  const changeSource = (idx: number, header: string | null) => {
    setMappings((prev) =>
      prev.map((m, i) => i === idx ? { ...m, sourceHeader: header, include: header !== null } : m)
    );
  };

  const handleConfirm = () => {
    const active = mappings.filter((m) => m.include && m.sourceHeader);
    const mapped = sourceData.map((row) => {
      const result: Record<string, any> = {};
      for (const m of active) result[m.targetCol.key] = row[m.sourceHeader!] ?? "";
      return result;
    });
    onConfirm(mapped);
  };

  return (
    <div style={{ ...overlay, zIndex: 10000 }}>
      <div style={{ backgroundColor: DLG_BG, border: DLG_BORDER, boxShadow: DLG_SHADOW, padding: "0 0 10px", minWidth: 520, ...DLG_FONT }}>
        <TitleBar title="Import Field Mapping" onClose={onCancel} />

        <div style={{ padding: "0 12px" }}>
          {/* Source / Target row */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 6, fontSize: 11, gap: 8 }}>
            <span><strong>Source:</strong> {filename}</span>
            <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
              <strong>Target:</strong>
              <span style={{ border: "2px inset #808080", backgroundColor: "#fff", padding: "1px 8px", fontSize: 11, minWidth: 160, display: "inline-block" }}>
                Current Table ("{tableName}")
              </span>
            </span>
          </div>

          {/* Column headers */}
          <div style={{ display: "flex", backgroundColor: "#d4d0c8", border: "1px solid #808080", borderBottom: "none" }}>
            <div style={{ width: 220, padding: "3px 8px", fontSize: 11, fontWeight: "bold", flexShrink: 0, borderRight: "1px solid #999" }}>Source Fields</div>
            <div style={{ width: 30, textAlign: "center", flexShrink: 0, borderRight: "1px solid #999" }} />
            <div style={{ flex: 1, padding: "3px 8px", fontSize: 11, fontWeight: "bold" }}>Target Fields</div>
          </div>

          {/* Rows */}
          <div style={{ border: "1px solid #808080", backgroundColor: "#fff", maxHeight: 280, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {mappings.map((m, idx) => {
                  const samples = m.sourceHeader ? (columnSamples[m.sourceHeader] || []) : [];
                  const isMapped = m.include && m.sourceHeader;
                  return (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                      {/* Source — dropdown + all unique non-empty values from every row */}
                      <td style={{ ...mappingTdBase, width: 220, flexShrink: 0, borderRight: "1px solid #d0d0d0", padding: "2px 4px" }}>
                        <select
                          value={m.sourceHeader ?? ""}
                          onChange={(e) => changeSource(idx, e.target.value || null)}
                          style={{ fontSize: 11, width: "100%", border: "none", background: "transparent", cursor: "pointer" }}
                        >
                          <option value="">— (don't import)</option>
                          {sourceHeaders.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                        </td>

                      {/* Arrow — click to toggle */}
                      <td
                        style={{ width: 30, textAlign: "center", cursor: "pointer", fontSize: 13, borderRight: "1px solid #d0d0d0", userSelect: "none", verticalAlign: "middle" }}
                        title={isMapped ? "Click to skip" : "Click to include"}
                        onClick={() => toggleInclude(idx)}
                      >
                        {isMapped ? <span style={{ color: "#000", fontWeight: "bold" }}>→</span> : <span style={{ color: "#888" }}>—</span>}
                      </td>

                      {/* Target field */}
                      <td style={{ ...mappingTdBase, color: isMapped ? "#000" : "#aaa", verticalAlign: "middle" }}>
                        <span style={{ marginRight: 4, color: "#555", fontSize: 9 }}>◆</span>
                        {m.targetCol.label}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legend row */}
          <div style={{ border: "1px solid #b0b0b0", backgroundColor: "#e8e8e8", padding: "4px 8px", marginTop: 6, display: "flex", gap: 16, fontSize: 11 }}>
            <span><strong>Field Mapping</strong></span>
            <span><b>→</b> Import this field</span>
            <span><b>—</b> Don't import this field</span>
          </div>

          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "#555", marginRight: "auto" }}>
              {sourceData.length} record{sourceData.length !== 1 ? "s" : ""} ready to import
            </span>
            <button type="button" style={grayBtn(70)} onClick={handleConfirm}>Import</button>
            <button type="button" style={grayBtn(70)} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CommonHeader ─────────────────────────────────────────────────────────────

const CommonHeader: React.FC<CommonHeaderProps> = ({
  backTo,
  className = "",
  currentRecord = "",
  disableNext = false,
  disablePrevious = false,
  exportColumns,
  exportFilename = "export",
  getData,
  hideNavigationDropdown = false,
  isFindMode = false,
  onBack,
  onCancelFind,
  onDeleteRecord,
  onFind,
  onImport,
  onNewRecord,
  onNextRecord,
  onPerformFind,
  onPreviousRecord,
  onRecordChange,
  onRecordCommit,
  onRecordSliderCommit,
  onShowAll,
  onApplySort,
  onSort,
  recordCount = 0,
  recordIndex = 0,
  recordInputLabel = "Records",
  rightContent,
  showAllDisabled = false,
  sortColumns,
  sortRules = [],
  sortTitle = "Sort Records",
  title,
  totalActiveCount,
  totalLabel = "Total (Unsorted)",
  totalRecordCount,
  totalValue = 0,
  withQuoteId,
}) => {
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const sliderRef = React.useRef<HTMLDivElement | null>(null);
  const sliderDragging = React.useRef(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = React.useState(false);
  const [sortDialogOpen, setSortDialogOpen] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [mappingState, setMappingState] = React.useState<{
    filename: string;
    sourceHeaders: string[];
    sampleRow: Record<string, any>;
    sourceData: Record<string, any>[];
  } | null>(null);
  const previousRecordIndex = React.useRef(recordIndex);
  const lastFindKeyRef = React.useRef(0);
  const inputValue = currentRecord === undefined || currentRecord === null ? "" : String(currentRecord);
  const safeRecordCount = Math.max(Number(recordCount) || 0, 0);
  const safeRecordIndex = Math.max(0, Math.min(Number(recordIndex) || 0, Math.max(safeRecordCount - 1, 0)));
  const displayRecordIndex = dragIndex ?? safeRecordIndex;
  const safeTotalCount = Math.max(Number(totalRecordCount ?? recordCount) || 0, 0);
  const safeActiveCount = Math.max(0, Math.min(Number(totalActiveCount ?? safeTotalCount) || 0, safeTotalCount));
  const isRecordFiltered = totalActiveCount !== undefined && safeTotalCount > 0 && safeActiveCount < safeTotalCount;
  const activePieDegrees = safeTotalCount > 0 ? (safeActiveCount / safeTotalCount) * 360 : 0;
  const recordIndicatorStyle: React.CSSProperties = {
    background: isRecordFiltered
      ? `conic-gradient(#1a7a1a 0deg ${activePieDegrees}deg, #42E35A ${activePieDegrees}deg 360deg)`
      : "#bdbdbd",
  };

  React.useEffect(() => {
    if (dragIndex !== null && recordIndex !== previousRecordIndex.current) {
      setDragIndex(null);
    }
    previousRecordIndex.current = recordIndex;
  }, [dragIndex, recordIndex]);

  // In find mode, pressing Enter anywhere on the page runs "Perform Find" so the
  // user doesn't have to reach for the toolbar button after typing criteria.
  React.useEffect(() => {
    if (!isFindMode || !onPerformFind) return;
    if (sortDialogOpen || exportDialogOpen || mappingState) return;

    const handleEnter = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.defaultPrevented || event.repeat) return;

      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "BUTTON" || tag === "A" || tag === "TEXTAREA") return;
        if (target.isContentEditable) return;
        // Let antd selects / dropdowns handle Enter as an option pick.
        if (target.closest(".ant-select, .ant-picker, .ant-dropdown, .ant-modal, .ant-cascader")) return;
      }

      event.preventDefault();
      // The page's own inputs may also bind Enter to Perform Find; collapse a
      // burst of Enter presses into a single search so we never double-fetch.
      const now = performance.now();
      if (now - lastFindKeyRef.current < 400) return;
      lastFindKeyRef.current = now;
      onPerformFind();
    };

    document.addEventListener("keydown", handleEnter);
    return () => document.removeEventListener("keydown", handleEnter);
  }, [isFindMode, onPerformFind, sortDialogOpen, exportDialogOpen, mappingState]);

  const handleBack = () => {
    if (onBack) { onBack(); return; }
    if (typeof backTo === "number") { navigate(backTo); return; }
    if (typeof backTo === "string") { navigate(backTo); return; }
    smartBack();
  };

  const handleRecordCommit = (value: string) => { onRecordCommit?.(value); };

  const getSliderIndex = (clientX: number) => {
    if (!sliderRef.current || safeRecordCount <= 1) return 0;
    const rect = sliderRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * (safeRecordCount - 1));
  };

  const handleSliderMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onRecordSliderCommit || safeRecordCount <= 1) return;
    sliderDragging.current = true;
    setDragIndex(getSliderIndex(event.clientX));
    const handleMouseMove = (e: MouseEvent) => { if (sliderDragging.current) setDragIndex(getSliderIndex(e.clientX)); };
    const handleMouseUp = (e: MouseEvent) => {
      sliderDragging.current = false;
      const idx = getSliderIndex(e.clientX);
      setDragIndex(idx);
      onRecordSliderCommit(idx);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImport) return;
    e.target.value = "";
    try {
      const rows = await readExcelFile(file);
      if (rows.length === 0) { message.warning("The file contains no data."); return; }
      // Collect ALL unique column keys across every row, excluding xlsx __EMPTY auto-keys
      const seen = new Set<string>();
      rows.forEach((r) => Object.keys(r).forEach((k) => {
        if (!k.startsWith("__EMPTY")) seen.add(k);
      }));
      const sourceHeaders = Array.from(seen);
      setMappingState({ filename: file.name, sourceHeaders, sampleRow: rows[0], sourceData: rows });
    } catch {
      message.error("Failed to read file. Please check the format.");
    }
  };

  const handleMappingConfirm = async (mappedData: Record<string, any>[]) => {
    if (!onImport) return;
    setMappingState(null);
    setImporting(true);
    try {
      await onImport(mappedData);
      message.success(`Imported ${mappedData.length} record(s) successfully.`);
      // Auto-refresh the page so the newly imported records are visible immediately
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Import failed.");
      setImporting(false);
    }
  };

  const showImportExport = !!(exportColumns || onImport);
  const canOpenSortDialog = !!(sortColumns?.length && onApplySort);
  const handleSortClick = () => {
    if (onSort) { onSort(); return; }
    if (canOpenSortDialog) setSortDialogOpen(true);
  };

  return (
    <>
      <div
        className={`common-header flex min-h-[62px] w-full items-center overflow-x-auto bg-gradient-to-b from-[#e8e8e8] to-[#c9c9c9] px-2 py-1 shadow-inner ${className}`.trim()}
      >
        <div className="flex items-center gap-2 border-r border-[#9a9a9a] pr-3">
          <button
            type="button"
            className="common-header-nav-btn common-header-back-btn flex items-center justify-center text-black"
            style={toolbarButtonStyle}
            onClick={handleBack}
            title="Back"
          >
            <LeftOutlined style={{ fontSize: 13 }} />
          </button>

          <div className="flex items-center">
            <button
              type="button"
              disabled={disablePrevious}
              className="common-header-nav-btn flex items-center justify-center text-black disabled:text-[#a8a8a8]"
              style={toolbarButtonStyle}
              onClick={onPreviousRecord}
              title="Previous record"
            >
              <CaretLeftOutlined style={{ fontSize: 14 }} />
            </button>
            <button
              type="button"
              disabled={disableNext}
              className="common-header-nav-btn flex items-center justify-center text-black disabled:text-[#a8a8a8]"
              style={toolbarButtonStyle}
              onClick={onNextRecord}
              title="Next record"
            >
              <CaretRightOutlined style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 border-r border-[#9a9a9a] px-4">
          {!hideNavigationDropdown && (
            <div className="flex min-w-[128px] flex-col">
              <NavigationDropdown withQuoteId={withQuoteId} className="!w-32" />
            </div>
          )}

          <div className="flex flex-col items-center">
            <input
              value={inputValue}
              onChange={(event) => onRecordChange?.(event.target.value)}
              onBlur={(event) => handleRecordCommit(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  // Own the key here so the find-mode Enter shortcut below stays out of it.
                  event.preventDefault();
                  handleRecordCommit((event.target as HTMLInputElement).value);
                }
              }}
              className="h-5 w-[72px] border border-[#7c7c7c] bg-white px-1 text-center text-[11px] font-normal text-black outline-none"
            />
            <div
              ref={sliderRef}
              className={`mt-[2px] flex h-[12px] w-[72px] items-center ${onRecordSliderCommit && safeRecordCount > 1 ? "cursor-pointer" : "cursor-default"}`}
              style={{ position: "relative", userSelect: "none" }}
              onMouseDown={handleSliderMouseDown}
            >
              <div className="absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 bg-white" />
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  left: safeRecordCount > 1
                    ? `calc(${(displayRecordIndex / (safeRecordCount - 1)) * 100}% - 2px)`
                    : "0px",
                  width: "4px",
                  height: "8px",
                  background: "#007ad9",
                }}
              />
            </div>
            <span className="mt-[4px] text-[10px] leading-none text-black">
              {recordInputLabel}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 border-r border-[#9a9a9a] px-4">
          <span
            className="h-6 w-6 rounded-full border border-[#6f6f6f] shadow-inner"
            style={recordIndicatorStyle}
          />
          <div className="flex min-w-[92px] flex-col leading-tight">
            <span className="text-[11px] text-black">{totalValue}</span>
            <span className="text-[10px] text-black">{totalLabel}</span>
          </div>
          <HeaderAction
            disabled={showAllDisabled}
            icon={<FileTextFilled style={{ color: "#9aa4ad" }} />}
            label="Show All"
            onClick={onShowAll}
          />
        </div>

        <div className="flex items-center gap-3 border-r border-[#9a9a9a] px-4">
          <HeaderAction
            icon={
              <>
                <FileTextFilled style={{ color: iconColor }} />
                <PlusOutlined className="absolute -right-1 bottom-0 rounded-full bg-white text-[8px] text-green-600" />
              </>
            }
            label="New Record"
            onClick={onNewRecord}
          />
          <HeaderAction
            icon={
              <>
                <FileTextFilled style={{ color: iconColor }} />
                <CloseOutlined className="absolute -right-1 bottom-0 rounded-full bg-white text-[8px] text-red-600" />
              </>
            }
            label="Delete Record"
            onClick={onDeleteRecord}
          />
        </div>

        <div className="flex items-center gap-3 px-4">
          {!isFindMode ? (
            <HeaderAction
              icon={
                <>
                  <SearchOutlined style={{ color: iconColor }} />
                  <CaretDownOutlined className="ml-[2px] text-[9px] text-black" />
                </>
              }
              label="Find"
              onClick={onFind}
            />
          ) : (
            <>
              <HeaderAction
                icon={
                  <>
                    <SearchOutlined style={{ color: iconColor }} />
                    <PlusOutlined className="absolute -right-1 bottom-0 rounded-full bg-white text-[8px] text-green-600" />
                  </>
                }
                label="Perform Find"
                onClick={onPerformFind}
              />
              <HeaderAction
                icon={
                  <>
                    <SearchOutlined style={{ color: iconColor }} />
                    <CloseOutlined className="absolute -right-1 bottom-0 rounded-full bg-white text-[8px] text-red-600" />
                  </>
                }
                label="Cancel Find"
                onClick={onCancelFind}
              />
            </>
          )}

          <HeaderAction
            disabled={!onSort && !canOpenSortDialog}
            icon={<SortAscendingOutlined style={{ color: iconColor }} />}
            label="Sort"
            onClick={handleSortClick}
          />
        </div>

        {/* Import / Export actions */}
        {showImportExport && (
          <div className="flex items-center gap-1 border-l border-[#9a9a9a] px-3">
            {exportColumns && getData && (
              <HeaderAction
                icon={<DownloadOutlined style={{ color: iconColor }} />}
                label="Export"
                onClick={() => setExportDialogOpen(true)}
              />
            )}
            {onImport && (
              <HeaderAction
                icon={
                  <span style={{ opacity: importing ? 0.4 : 1 }}>
                    <UploadOutlined style={{ color: iconColor }} />
                  </span>
                }
                label={importing ? "Importing…" : "Import"}
                onClick={() => !importing && fileInputRef.current?.click()}
              />
            )}
          </div>
        )}

        {rightContent ? (
          <div className="ml-auto flex items-center pl-4">{rightContent}</div>
        ) : null}
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />

      {/* FileMaker-style export dialog */}
      {exportDialogOpen && exportColumns && getData && (
        <ExportDialog
          columns={exportColumns}
          getData={getData}
          filename={exportFilename}
          onClose={() => setExportDialogOpen(false)}
        />
      )}

      {/* FileMaker-style sort dialog */}
      {sortDialogOpen && sortColumns && onApplySort && (
        <SortDialog
          columns={sortColumns}
          initialRules={sortRules}
          onApply={onApplySort}
          onClose={() => setSortDialogOpen(false)}
          title={sortTitle}
        />
      )}

      {/* Import field mapping dialog */}
      {mappingState && exportColumns && (
        <ImportMappingDialog
          filename={mappingState.filename}
          tableName={exportFilename.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          sourceHeaders={mappingState.sourceHeaders}
          sampleRow={mappingState.sampleRow}
          sourceData={mappingState.sourceData}
          targetColumns={exportColumns}
          onConfirm={handleMappingConfirm}
          onCancel={() => setMappingState(null)}
        />
      )}
    </>
  );
};

export default CommonHeader;
